import React, { useState, useEffect } from "react";

// BackToTop - circular floating button with progress ring
// Always dark mode for this site
const BackToTop = ({ showAfter = 400 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? Math.min(scrollY / docHeight, 1) : 0;
      setIsVisible(scrollY > showAfter);
      setProgress(pct);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [showAfter]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // SVG ring math
  const size = 52;
  const stroke = 2;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - progress);

  return (
    <div
      className={`fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-50 transition-all duration-500 ease-out ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      <button
        onClick={scrollToTop}
        aria-label="Back to top"
        className="group relative block"
        style={{ width: size, height: size }}
      >
        {/* hover glow */}
        <span
          className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(74,158,245,0.4) 0%, transparent 70%)",
            filter: "blur(8px)",
            transform: "scale(1.4)",
          }}
        />

        {/* progress ring */}
        <svg width={size} height={size} className="absolute inset-0 -rotate-90">
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} fill="none"
          />
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            stroke="url(#btt-gradient)" strokeWidth={stroke} fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.15s linear" }}
          />
          <defs>
            <linearGradient id="btt-gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#4A9EF5" />
              <stop offset="100%" stopColor="#5DE9B8" />
            </linearGradient>
          </defs>
        </svg>

        {/* inner button */}
        <span
          className="absolute inset-1 rounded-full flex items-center justify-center backdrop-blur-xl transition-all duration-300 group-hover:scale-95 bg-[#0a0a0c]/80 border border-white/[0.08]"
          style={{
            boxShadow: "0 8px 20px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
        >
          {/* arrow up icon inline */}
          <svg
            className="w-4 h-4 text-blue-400 transition-transform duration-300 group-hover:-translate-y-0.5"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </span>
      </button>
    </div>
  );
};

export default BackToTop;